import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface AnimatedTextProps {
  text: string;
  isSpeaking: boolean;
  onComplete?: () => void;
  className?: string;
}

const AnimatedText: React.FC<AnimatedTextProps> = ({ 
  text, 
  isSpeaking, 
  onComplete,
  className = '' 
}) => {
  const [words, setWords] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (text) {
      setWords(text.split(' '));
      setCurrentIndex(0);
    }
  }, [text]);

  useEffect(() => {
    if (isSpeaking && currentIndex < words.length) {
      const wordDuration = 200; // Duração média por palavra
      const timer = setTimeout(() => {
        setCurrentIndex(prev => {
          if (prev + 1 >= words.length) {
            onComplete?.();
            return prev;
          }
          return prev + 1;
        });
      }, wordDuration);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, isSpeaking, words.length]);

  return (
    <div className={`flex flex-wrap justify-center gap-1 ${className}`}>
      <AnimatePresence mode="popLayout">
        {words.slice(0, currentIndex + 1).map((word, index) => (
          <motion.span
            key={`${word}-${index}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              scale: index === currentIndex ? 1.05 : 1
            }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ 
              type: "spring",
              stiffness: 200,
              damping: 20,
              opacity: { duration: 0.3 },
              scale: { duration: 0.3 }
            }}
            className={index === currentIndex ? 'text-primary/80' : ''}
          >
            {word}
          </motion.span>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default AnimatedText;
